import { useCloseContextMenuOnScroll } from '@hooks/useCloseContextMenuOnScroll';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { CatalogUerBaseCostData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useRef, useState } from 'react';

import { useRemoveUerBaseCost } from './hooks/useRemoveBaseCost';

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');

interface IRowMenu {
    handleOpenModalEstimate: (isEdit?: boolean) => void;
    row: CatalogUerBaseCostData;
    setRowData: (row: CatalogUerBaseCostData) => void;
    onRemoveSuccess: () => void;
    onEstimateBasisModalOpen: () => void;
    canRead: boolean;
    canEdit: boolean;
    canDelete: boolean;
}

const contextMenuStyle = { width: 180 };

export const RowMenu = ({
    handleOpenModalEstimate,
    onEstimateBasisModalOpen,
    row,
    setRowData,
    onRemoveSuccess,
    canRead,
    canEdit,
    canDelete,
}: IRowMenu) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const ref = useRef(null);

    useCloseContextMenuOnScroll({ setOpen: setIsOpen });

    const { catalogRemoveBaseCostRequest } = useRemoveUerBaseCost([row.id], onRemoveSuccess);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveBaseCostRequest(),
        });

    const handleOnClick = useCallback(() => {
        setIsOpen(R.not);
    }, []);

    const handleOpenEditing = useCallback(() => {
        setRowData(row);
        handleOpenModalEstimate(true);
    }, [handleOpenModalEstimate, row, setRowData]);

    const handleEstimateBasisModalOpen = useCallback(() => {
        setRowData(row);
        onEstimateBasisModalOpen();
    }, [onEstimateBasisModalOpen, row, setRowData]);

    const items = [
        {
            label: 'Редактировать',
            action: handleOpenEditing,
            disabled: !canEdit,
        },
        {
            label: 'Просмотреть смету-основание',
            action: handleEstimateBasisModalOpen,
            disabled: !canRead,
        },
        {
            label: 'Удалить',
            action: handleOpenConfirmationDialog,
            disabled: !canDelete,
        },
    ];

    const handleItemOnClick = useCallback(
        (item) => () => {
            const action = getItemOnClick(item) as () => void;

            action();
            setIsOpen(false);
        },
        [],
    );

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                style={{ margin: '0px 4px' }}
                disabled={!canRead && !canEdit && !canDelete}
                onClick={handleOnClick}
                ref={ref}
            />

            <ContextMenu
                isOpen={isOpen}
                items={items}
                getItemLabel={getItemLabel}
                anchorRef={ref}
                direction="downStartLeft"
                getItemOnClick={handleItemOnClick}
                onClickOutside={handleOnClick}
                style={contextMenuStyle}
            />
            <ConfirmationDialog
                textHeader="УДАЛЕНИЕ ЗАПИСИ"
                textConfirmation="Вы уверены,что хотите удалить запись?"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
            />
        </>
    );
};

RowMenu.displayName = 'RowMenu';
